import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, ArrowLeft, Search } from "lucide-react";
import { EASING, DURATION } from "@/lib/motionSystem";

export default function NotFound() {
    const navigate = useNavigate();
    return (
        <div className="min-h-dvh pt-24 pb-20 px-6 flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                className="text-center max-w-lg"
            >
                {/* 404 number */}
                <div className="font-mono text-[8rem] sm:text-[10rem] font-bold leading-none mb-4 select-none"
                    style={{
                        background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 50%, #a855f7 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        filter: "drop-shadow(0 0 60px rgba(99,102,241,0.3))",
                    }}
                >
                    404
                </div>

                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-text-primary mb-3">
                    Page not found
                </h1>
                <p className="text-text-secondary text-base mb-8 leading-relaxed">
                    The page you're looking for doesn't exist or has been moved.
                    Let's get you back on track.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                        to="/"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-primary text-white font-semibold hover:bg-blue-primary/90 hover:shadow-[0_0_20px_rgba(99,102,241,0.4)] transition-all"
                    >
                        <Home size={16} /> Go Home
                    </Link>
                    {/* Bug 5: Go Back must actually go back to the previous page in browser
                        history, not to home. Falls back to home if there's no history
                        (user landed directly on a broken URL with no prior page). */}
                    <button
                        onClick={() => {
                            if (window.history.length > 1) {
                                navigate(-1);
                            } else {
                                navigate("/", { replace: true });
                            }
                        }}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-text-secondary hover:text-text-primary hover:bg-white/[0.05] transition-all"
                    >
                        <ArrowLeft size={16} /> Go Back
                    </button>
                </div>

                <div className="mt-10 pt-8 border-t border-white/5">
                    <p className="text-text-secondary text-xs mb-4">Looking for something specific?</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {[
                            { to: "/genz-credit-scoring/flow", label: "Get Score" },
                            { to: "/genz-credit-scoring/compare", label: "Compare Scores" },
                            { to: "/genz-credit-scoring/history", label: "My History" },
                            { to: "/privacy-policy", label: "Privacy Policy" },
                        ].map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-text-secondary hover:text-cyan-signal hover:border-cyan-signal/30 transition-all"
                            >
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
